import type {
  BatchTransitionContextValue,
  BatchTransitionFormKey,
  BatchTransitionFormState,
  BatchTransitionProviderProps,
  BatchTransitionState,
} from "./batch-transition.type";
import type {
  TransitionableStateModelItem,
  TransitionableStateModelType,
  TransitionableStateValue,
} from "@/entities/state";
import type { MutationStatus } from "@tanstack/react-query";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useReferentialTypeStates } from "@/entities/referential/referential-states";
import { useSidePanel } from "@/modules/side-panel";
import { groupBy, uniq } from "@/utilities/array";

import { useBatchTransitionContext } from "./batch-transition.context";

export const useDisableBatchTransition = (
  type: TransitionableStateModelType,
  items: TransitionableStateModelItem[]
) => {
  const { t } = useTranslation();
  const { states } = useReferentialTypeStates(type);

  const hasNextStates = uniq(items.map((i) => i.next_states.length > 0));
  const disabled = !hasNextStates.includes(true);

  const editableStates = states
    .filter((state) => state.transition_to?.length)
    .map((state) => state.name)
    .join(", ");

  const tooltip = t("global:batchTransition.stateDisabledTooltip", {
    states: editableStates,
  });

  return {
    disabled,
    tooltip: disabled ? tooltip : undefined,
  };
};

export const useBatchTransitionContextData = ({
  type,
  items,
}: BatchTransitionProviderProps): BatchTransitionContextValue => {
  const {
    forms,
    hasChanges,
    isLoading,
    isFinished,
    isSuccess,
    setFormState,
    setFormStatus,
    resetFormState,
  } = useBatchTransitionState();

  const { closeSidePanel } = useSidePanel();
  const { states } = useReferentialTypeStates(type);

  const itemsByState = groupBy(items, ({ state }) => state);
  const selectedStates = Object.keys(itemsByState);
  const count = items.length;

  // Use states from referential as a base value because they are sorted
  // based on the default sort. Then only keep selected states.
  const finalStates = states
    .map((state) => state.value)
    .filter((state) => selectedStates.includes(state));

  useEffect(() => {
    if (isSuccess) closeSidePanel();
  }, [isSuccess]);

  const onStateValueChange = (
    initialValue: TransitionableStateValue,
    formState: BatchTransitionFormState
  ) => {
    if (initialValue === formState.value) resetFormState(initialValue);
    else setFormState(initialValue, formState);
  };

  const onSubmitClick = () => {
    if (!hasChanges) return;
    forms.forEach((form) => form.submit());
  };

  return {
    type,
    items,
    itemsByState,
    count,
    states: finalStates as TransitionableStateValue[],
    referentialStates: states,
    hasChanges,
    isLoading,
    isFinished,
    isSuccess,
    onStateValueChange,
    onSubmitClick,
    onMutationStatusChange: setFormStatus,
  };
};

export const useBatchTransitionState = () => {
  const [state, setState] = useState<BatchTransitionState>({});

  const hasChanges = Object.keys(state).length > 0;
  const forms = Object.values(state).map((value) => value.form);
  const formsStatus = Object.values(state).map((value) => value.status);

  const isLoading = formsStatus.includes("pending");
  const isFinished = hasChanges && !isLoading && !formsStatus.includes("idle");
  const isSuccess = isFinished && !formsStatus.includes("error");

  const setFormState = (
    formKey: BatchTransitionFormKey,
    formState: BatchTransitionFormState
  ) => {
    setState((state) => ({ ...state, [formKey]: formState }));
  };

  const setFormStatus = (
    formKey: BatchTransitionFormKey,
    status: MutationStatus
  ) => {
    if (!state[formKey]) return;
    setState((state) => ({
      ...state,
      [formKey]: { ...state[formKey], status },
    }));
  };

  const resetFormState = (formKey: BatchTransitionFormKey) => {
    setState((state) => {
      const newState = { ...state }; // create new object to make sure state update
      delete newState[formKey];
      return newState;
    });
  };

  return {
    forms,
    hasChanges,
    isLoading,
    isFinished,
    isSuccess,
    setFormState,
    setFormStatus,
    resetFormState,
  };
};

export const useBatchTransitionRowContext = (
  state: TransitionableStateValue
) => {
  const contextData = useBatchTransitionContext();
  const { itemsByState } = contextData;

  const rowItems = itemsByState[state] ?? [];
  const rowCount = rowItems.length;

  return {
    ...contextData,
    rowItems,
    rowCount,
  };
};
